import {
  EventCategory,
  TriggerAssetScope,
  TriggerAssignmentStrategy,
} from '../../types';

/**
 * Display metadata for trigger + event-definition enums. Single source
 * of truth so the picker, the table, and any filters all show the same
 * labels.
 */

// ─── Event categories ────────────────────────────────────────────────────────

export const EVENT_CATEGORY_OPTIONS: {
  value: EventCategory;
  label: string;
  hint: string;
}[] = [
  {
    value: 'schedule',
    label: 'Schedule',
    hint: 'Time-based — cron, daily / weekly / monthly, or a specific date',
  },
  {
    value: 'external',
    label: 'External',
    hint: 'Events outside your world — feeds, news, market data, webhooks',
  },
  {
    value: 'internal',
    label: 'Internal',
    hint: 'Your own data — email, portfolio, notes, drawdown thresholds',
  },
  {
    value: 'workflow',
    label: 'Workflow',
    hint: "Reactions to another workflow's lifecycle — started / completed",
  },
];

export const getEventCategoryLabel = (value: EventCategory): string =>
  EVENT_CATEGORY_OPTIONS.find(o => o.value === value)?.label ?? value;

// ─── Per-category sub-config option lists ────────────────────────────────────

export const SCHEDULE_TYPE_OPTIONS = [
  { value: 'one_time', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'cron', label: 'Custom (cron)' },
];

export const DAY_OF_WEEK_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

export const INTERNAL_SOURCE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'note', label: 'Note' },
  { value: 'custom', label: 'Custom' },
];

export const WORKFLOW_LIFECYCLE_OPTIONS = [
  { value: 'started', label: 'Started' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ─── Asset scope ─────────────────────────────────────────────────────────────

export const ASSET_SCOPE_OPTIONS: {
  value: TriggerAssetScope;
  label: string;
  hint: string;
}[] = [
  {
    value: 'coverage',
    label: 'By coverage',
    hint: 'Fires per coverage relationship — assignee follows the coverage owner',
  },
  {
    value: 'event',
    label: 'By event',
    hint: 'Fires for the asset named in the event payload — pick an assignment strategy',
  },
];

export const getAssetScopeLabel = (value: TriggerAssetScope): string =>
  ASSET_SCOPE_OPTIONS.find(o => o.value === value)?.label ?? value;

// ─── Assignment strategy ─────────────────────────────────────────────────────
// Only relevant when asset_scope === 'event'. Coverage-scoped triggers
// inherit the assignee from the coverage relationship.

export const ASSIGNMENT_STRATEGY_OPTIONS: {
  value: TriggerAssignmentStrategy;
  label: string;
  hint: string;
  comingSoon?: boolean;
}[] = [
  {
    value: 'round_robin',
    label: 'Round-robin',
    hint: 'Rotates through team members',
  },
  {
    value: 'least_loaded',
    label: 'Least-loaded worker',
    hint: 'Goes to the worker with the fewest active assignments',
  },
  {
    value: 'specific_user',
    label: 'Specific user',
    hint: 'Always goes to a fixed team member',
  },
  {
    value: 'first_to_accept',
    label: 'First to accept',
    hint: 'Open to the team — first worker to accept gets it',
    comingSoon: true,
  },
];

export const getAssignmentStrategyLabel = (value: TriggerAssignmentStrategy): string =>
  ASSIGNMENT_STRATEGY_OPTIONS.find(o => o.value === value)?.label ?? value;
